import { AHIcon, AHIconType, AHSeparator, AHText, Box, ColorType } from "arkh";
import React, { useRef, useState } from "react";
import { GestureResponderEvent, Platform, View } from "react-native";
import { AbsolutePopover } from "../components/mobile/AbsolutePopover";
import { PopoverListItem } from "../components/mobile/Popover";

export type MoreItem = {
  label: React.ReactNode;
  icon?: AHIconType;
  onPress: () => void;
  color?: ColorType;
  type?: "navigation" | "button" | "selected-check";
};

export function MoreButton(props: {
  items: MoreItem[];
  offset?: number;
  header?: React.ReactNode;
  children: (onOpen: () => void) => React.ReactNode;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [pageOffset, setPageOffset] = useState({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0, w: 10 });
  const ref = useRef<View>(null);

  const onOpen = () => {
    ref.current?.measure((x, y, width, height, pageX, pageY) => {
      setPos({
        x: pageOffset.x + pageX,
        y: pageOffset.y + pageY + height + (props.offset ?? 0),
        w: width,
      });
      setMoreOpen(true);
    });
  };

  // pageX/pageY är relativt till sheetet istället för skärmen på iOS om MoreButton ligger i en stack
  // touch-eventet används för att beräkna positionen av sheetet
  const onTouchStart =
    Platform.OS === "ios"
      ? (e: GestureResponderEvent) => {
          const event = e as GestureResponderEvent & {
            nativeEvent: { screenX?: number; screenY?: number };
          };
          if (
            typeof event.nativeEvent.screenX === "number" &&
            typeof event.nativeEvent.screenY === "number"
          ) {
            const newPageOffset = {
              x: event.nativeEvent.screenX - event.nativeEvent.pageX,
              y: event.nativeEvent.screenY - event.nativeEvent.pageY,
            };
            setPageOffset(newPageOffset);
          }
        }
      : undefined;

  return (
    <View ref={ref} collapsable={false} onTouchStart={onTouchStart}>
      <Box spaceLeft="medium" />
      {props.children(onOpen)}
      {moreOpen ? (
        <AbsolutePopover
          top={pos.y ?? 0}
          left={pos.x ?? 0}
          buttonWidth={pos.w}
          onClose={() => {
            setMoreOpen(false);
          }}
        >
          {props.items.map((item, index) => {
            const detail =
              item.type === "selected-check"
                ? "selected-check"
                : item.type === "button"
                  ? "none"
                  : "chevron";
            return (
              <React.Fragment key={index}>
                {index === 0 ? null : <AHSeparator />}
                <PopoverListItem
                  detail={detail}
                  onPress={() => {
                    item.onPress();
                    setMoreOpen(false);
                  }}
                >
                  {item.icon && <AHIcon type={item.icon} color={item.color} />}
                  <AHText
                    spaceHorizontal="small"
                    type="list-item-title"
                    style={{ flex: 1 }}
                    selectable={false}
                    color={item.color}
                  >
                    {item.label}
                  </AHText>
                </PopoverListItem>
              </React.Fragment>
            );
          })}
        </AbsolutePopover>
      ) : null}
    </View>
  );
}
